import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { api, type Me } from "../api";
import { C, font } from "./look";

// P1 header: the SAVON wordmark "for pharmacies", the pharmacy and its area,
// and the switch that opens or pauses taking orders.
export function PharmacyHeader({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const p = me.pharmacy;
  const toggle = async () => {
    if (!p || busy) return;
    if (p.open && !window.confirm("Pause taking orders? Customers will not be able to order until you open again.")) return;
    setBusy(true);
    try {
      await api("/pharmacy/open", { body: { open: !p.open } });
      await qc.invalidateQueries({ queryKey: ["me"] });
    } finally {
      setBusy(false);
    }
  };
  return (
    <header className="no-print" style={{ background: C.surface, borderBottom: `1px solid ${C.line}`, fontFamily: font.sans, color: C.ink }}>
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "12px 20px",
          boxSizing: "border-box",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <span style={{ fontFamily: font.display, fontWeight: 800, fontSize: 20, letterSpacing: "0.01em" }}>SAVON</span>
          <span style={{ fontSize: 13, color: C.muted }}>for pharmacies</span>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
          {p && (
            <span style={{ fontSize: 15, fontWeight: 600 }}>
              {p.name}, {p.zoneName.replace(/\s*\(.*\)$/, "")}
            </span>
          )}
          {p && (
            <button
              role="switch"
              aria-checked={p.open}
              disabled={busy}
              onClick={toggle}
              style={{
                height: 40,
                padding: "0 14px 0 6px",
                borderRadius: 999,
                border: `1px solid ${C.line}`,
                background: p.open ? C.tint : C.surface,
                color: C.ink,
                fontSize: 13,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
              }}
            >
              <span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: 999, background: p.open ? C.teal : C.line, display: "block" }} />
              {p.open ? "Open, taking orders" : "Paused, not taking orders"}
            </button>
          )}
          <button onClick={onSignOut} style={{ height: 40, padding: "0 14px", borderRadius: 999, border: `1px solid ${C.line}`, background: C.surface, fontSize: 13, fontWeight: 600, color: C.muted, cursor: "pointer" }}>
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
}
